import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Icon,
  Label,
  Modal,
  ModalContent,
  ProgressBar,
  Space,
  Stack,
  TextField,
  Upload,
} from 'mw-style-react';

import {
  DEL_MODAL,
  PROMPT_NODE_ACCEPT_FILES,
  UPLOAD_FILE,
} from '@control-front-end/common/constants';
import { useIntl, useNotifications } from 'hooks';
import { CREATE_ACTOR_WITH_AI } from '@control-front-end/common/constants/graphActors';
import { FilesDragZone } from '@control-front-end/app/src/components';
import SelectActorsTemplate from '@control-front-end/common/components/SelectActorsTemplate';
import mes from './intl';
// eslint-disable-next-line no-unused-vars
import sModal from '../../Modal.scss'; // NOSONAR
// eslint-disable-next-line no-unused-vars
import sLocal from './CreateActorWithAI.scss';

const ERROR_NO_FORM_SELECTED = 'noFormSelected';
const ERROR_NO_TEXT_NOR_FILES = 'noTextNorFiles';

const defaultActorData = {
  files: [],
  text: '',
  form: { id: null },
  actorName: '',
};

function CreateActorWithAI(props) {
  const {
    visibility,
    onClose,
    data: { formId: initFormId, callback },
    modalName,
  } = props;
  const t = useIntl();
  const dispatch = useDispatch();
  const uploadZoneRef = useRef();
  const filesListRef = useRef();
  const showNotification = useNotifications();
  const config = useSelector((state) => state.config);
  const [isLoading, setLoading] = useState(false);
  const [actorData, setActorData] = useState({
    ...defaultActorData,
    form: { id: initFormId },
  });

  const handleChange = ({ id, value }) => {
    setActorData((prevActorData) => ({ ...prevActorData, [id]: value }));
  };

  const handleCreateActorWithAI = (fileName) => {
    const { text, form, actorName } = actorData;
    dispatch({
      type: CREATE_ACTOR_WITH_AI.REQUEST,
      payload: {
        fileName,
        text,
        actorTitle: actorName,
        formId: form.id,
      },
      callback: (actor) => {
        setLoading(false);
        if (callback) callback(actor);
        dispatch({ type: DEL_MODAL, payload: modalName });
      },
      errorCallback: () => {
        setLoading(false);
      },
    });
  };

  const handleUpload = (data) => {
    handleChange({ id: 'files', value: data.value });
  };

  const validateErrors = () => {
    if (!actorData.form.id) {
      return ERROR_NO_FORM_SELECTED;
    }
    if (!actorData.text && !actorData.files.length) {
      return ERROR_NO_TEXT_NOR_FILES;
    }
    return null;
  };

  const handleSubmit = () => {
    const actorDataError = validateErrors();
    if (actorDataError) {
      showNotification.error(t(mes[actorDataError]));
      return;
    }
    setLoading(true);
    const { files } = actorData;
    if (!files.length) {
      handleCreateActorWithAI();
      return;
    }
    dispatch({
      type: UPLOAD_FILE.REQUEST,
      payload: {
        files,
        callback: (attachments) => {
          const fileName = attachments.map(({ fileName }) => fileName);
          handleCreateActorWithAI(fileName);
        },
      },
    });
  };

  const renderLabelWithLoader = () => (
    <Stack.H fullWidth justifyContent="center">
      <Label value={t(mes.createActor)} />
      {isLoading ? <ProgressBar type="circle" size="small" /> : null}
    </Stack.H>
  );

  return (
    <Modal
      id="createFolderModal"
      styleName="sModal.modal sLocal.modal"
      visibility={visibility}
      onClose={onClose}
      label={t(mes.createActorWithAI)}
      size="large"
      dataTestId="modal-create-folder"
    >
      <ModalContent styleName="sModal.modal__content sLocal.content">
        <Stack.V size={Stack.SIZE.large}>
          <FilesDragZone
            containerRef={uploadZoneRef}
            skipChildren={true}
            hidden
            multiple
            onUpload={handleUpload}
          />
          <Stack.V fullWidth size={Stack.SIZE.none}>
            <Label value={t(mes.file)} fontWeight={Label.WEIGHTS.semibold} />
            <Upload
              value={actorData.files}
              multiple
              preview
              accept={PROMPT_NODE_ACCEPT_FILES}
              maxFileSize={config.maxFileSize}
              classes={{ customBox: sLocal.customBox }}
              onChange={handleUpload}
              portal={filesListRef.current}
            >
              <Space
                forwardRef={uploadZoneRef}
                size={Space.SIZE.large}
                fullWidth
              >
                <Stack.V
                  justifyContent="center"
                  alignItems="center"
                  fullWidth
                  size={Stack.SIZE.medium}
                >
                  <Stack.V
                    justifyContent="center"
                    alignItems="center"
                    fullWidth
                    size={Stack.SIZE.xxsmall}
                  >
                    <Icon type="files" size="large" />
                    <Label
                      value={t(mes.dropFilesHere)}
                      fontWeight={Label.WEIGHTS.semibold}
                    />
                    <Label value={t(mes.supportsFileTypes)} />
                  </Stack.V>
                  <div ref={filesListRef} className={sLocal.filesList} />
                </Stack.V>
              </Space>
            </Upload>
          </Stack.V>
          <TextField
            id="text"
            label={
              <Label value={t(mes.text)} fontWeight={Label.WEIGHTS.semibold} />
            }
            value={actorData.text}
            helperText={t(mes.textHelper)}
            placeholder={t(mes.textPlaceholder)}
            onChange={handleChange}
            multiline
            multilineType="text"
            rows={4}
            autoFocus
            bordered
            unspaced
            dataTestId="modal-create-folder-name-input"
          />
          <SelectActorsTemplate
            id="form"
            label={
              <Label
                value={t(mes.selectForm)}
                fontWeight={Label.WEIGHTS.semibold}
              />
            }
            value={actorData.form}
            placeholder={t(mes.formPlaceholder)}
            helperText={t(mes.formHelper)}
            withDefault={false}
            withRelations={true}
            fullModel
            bordered
            unspaced
            formTypes="all"
            type="modal"
            onChange={handleChange}
            dataTestId="actor-filter-forms"
          />
          <TextField
            id="actorName"
            label={
              <Label
                value={t(mes.actorName)}
                fontWeight={Label.WEIGHTS.semibold}
              />
            }
            value={actorData.actorName}
            helperText={t(mes.actorNameHelper)}
            placeholder={t(mes.actorNamePlaceholder)}
            onChange={handleChange}
            bordered
            unspaced
            dataTestId="modal-create-folder-name-input"
          />
        </Stack.V>
      </ModalContent>
      <Space>
        <Stack.H
          fullWidth
          size={Stack.SIZE.medium}
          justifyContent="spaceBetween"
        >
          <Button
            size="smallplus"
            fullWidth
            label={renderLabelWithLoader()}
            onClick={handleSubmit}
            disabled={isLoading}
            visibility={isLoading ? 'disabled' : 'visible'}
            dataTestId="modal-create-folder-submit"
          />
        </Stack.H>
      </Space>
    </Modal>
  );
}

CreateActorWithAI.defaultProps = {
  modalName: 'CreateActorWithAI',
};

CreateActorWithAI.propTypes = {
  visibility: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  data: PropTypes.shape({
    callback: PropTypes.func,
  }),
  modalName: PropTypes.string,
};

export default CreateActorWithAI;
